import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaUpload } from "react-icons/fa";

const FaceRecognition = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setResult(null); // Reset result on new upload
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);

    // Simulate API call
    setTimeout(() => {
      // Mock result from backend
      setResult({
        detectedFaces: 3,
        message: "Faces successfully recognized!",
      });
      setIsProcessing(false);
    }, 3000); // Replace with actual API integration
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
      {/* Header Section */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <h1 className="text-5xl md:text-7xl font-extrabold">
          Real-Time Face Recognition
        </h1>
        <p className="mt-4 text-lg md:text-xl text-gray-400">
          Upload a photo and let our AI detect faces instantly.
        </p>
      </motion.div>

      {/* Upload Section */}
      <motion.div
        className="bg-gray-800 p-8 rounded-xl shadow-lg max-w-lg w-full"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        {!selectedFile ? (
          <div className="flex flex-col items-center">
            <label
              htmlFor="file-upload"
              className="cursor-pointer bg-gray-700 p-6 rounded-lg flex flex-col items-center hover:bg-gray-600 transition duration-300"
            >
              <FaUpload className="text-4xl mb-2" />
              <span className="text-lg">Drag & Drop or Click to Upload</span>
            </label>
            <input
              id="file-upload"
              type="file"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        ) : (
          <div className="text-center">
            <p className="text-lg mb-4">{selectedFile.name}</p>
            <button
              onClick={handleUpload}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
            >
              {isProcessing ? "Processing..." : "Analyze Image"}
            </button>
          </div>
        )}
      </motion.div>

      {/* Processing State */}
      {isProcessing && (
        <motion.div
          className="mt-12 text-lg text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-gray-400">Processing your image...</p>
          <div className="loader mt-4"></div>
        </motion.div>
      )}

      {/* Result Display */}
      {result && (
        <motion.div
          className="mt-12 bg-gray-800 p-6 rounded-xl shadow-lg text-center max-w-md w-full"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h2 className="text-2xl font-bold">Analysis Result</h2>
          <p className="mt-4 text-lg text-gray-300">
            {result.message}
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Detected Faces: {result.detectedFaces}
          </p>
        </motion.div>
      )}

      {/* Styles for Loader */}
      <style>{`
        .loader {
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-left-color: white;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default FaceRecognition;